import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Book,
  Search,
  Home,
  LayoutDashboard,
  ClipboardList,
  Users,
  Stethoscope,
  Pill,
  BarChart3,
  Settings,
  Zap,
  ScanLine,
  Shield,
  HelpCircle,
  ChevronRight,
  ChevronDown,
  Lightbulb,
  AlertTriangle,
  Info,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

// Section icons mapping
const sectionIcons = {
  gettingStarted: Home,
  dashboard: LayoutDashboard,
  orders: ClipboardList,
  patients: Users,
  specialists: Stethoscope,
  medications: Pill,
  reports: BarChart3,
  settings: Settings,
  automation: Zap,
  ocr: ScanLine,
  security: Shield,
  faq: HelpCircle
};

// Tip component
const Tip = ({ type = 'tip', children }) => {
  const { t } = useTranslation('documentation');
  const icons = {
    tip: Lightbulb,
    warning: AlertTriangle,
    note: Info
  };
  const colors = {
    tip: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200',
    warning: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200',
    note: 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-gray-800/50 dark:border-gray-700 dark:text-gray-200'
  };
  const Icon = icons[type];

  return (
    <div className={`flex gap-3 p-4 rounded-lg border ${colors[type]} my-4`}>
      <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
      <div>
        <span className="font-semibold">{t(`tips.${type}`)}: </span>
        {children}
      </div>
    </div>
  );
};

// Step list component
const StepList = ({ steps }) => {
  // Handle cases where steps is not an array
  if (!steps || !Array.isArray(steps)) {
    return null;
  }
  
  return (
    <ol className="list-none space-y-3 my-4">
      {steps.map((step, index) => (
        <li key={index} className="flex gap-3 items-start">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm font-semibold flex items-center justify-center">
            {index + 1}
          </span>
          <span className="text-gray-700 dark:text-gray-300">{step}</span>
        </li>
      ))}
    </ol>
  );
};

// Feature list component
const FeatureList = ({ items }) => {
  // Handle cases where items is not an array
  if (!items || !Array.isArray(items)) {
    return null;
  }
  
  return (
    <ul className="space-y-2 my-4">
      {items.map((item, index) => (
        <li key={index} className="flex gap-2 items-start text-gray-700 dark:text-gray-300">
          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

// Section content renderer
const SectionContent = ({ sectionKey }) => {
  const { t } = useTranslation('documentation');

  const renderGettingStarted = () => (
    <div className="space-y-8">
      {/* Welcome */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('gettingStarted.welcome.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('gettingStarted.welcome.description')}
        </p>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('gettingStarted.welcome.purpose')}
        </p>
        <h4 className="font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Funcionalidades principales:</h4>
        <FeatureList items={t('gettingStarted.welcome.features', { returnObjects: true })} />
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          {t('gettingStarted.welcome.targetUsers')}
        </p>
      </div>

      {/* Login */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('gettingStarted.login.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('gettingStarted.login.description')}
        </p>
        <StepList steps={t('gettingStarted.login.steps', { returnObjects: true })} />
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('gettingStarted.login.forgotPassword')}
        </p>
        <h4 className="font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">{t('gettingStarted.login.securityRequirements')}</h4>
        <FeatureList items={t('gettingStarted.login.passwordRules', { returnObjects: true })} />
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          {t('gettingStarted.login.sessionSecurity')}
        </p>
        <Tip type="tip">{t('gettingStarted.login.tip')}</Tip>
      </div>

      {/* Navigation */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('gettingStarted.navigation.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('gettingStarted.navigation.description')}
        </p>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('gettingStarted.navigation.sidebarDescription')}
        </p>
        <FeatureList items={t('gettingStarted.navigation.items', { returnObjects: true })} />
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          {t('gettingStarted.navigation.headerBar')}
        </p>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('gettingStarted.navigation.collapseSidebar')}
        </p>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('gettingStarted.navigation.breadcrumbs')}
        </p>
        <Tip type="tip">{t('gettingStarted.navigation.tip')}</Tip>
      </div>

      {/* Quick Start */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('gettingStarted.quickStart.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('gettingStarted.quickStart.description')}
        </p>
        <StepList steps={t('gettingStarted.quickStart.steps', { returnObjects: true })} />
      </div>
    </div>
  );

  const renderDashboard = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('dashboard.overview.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('dashboard.overview.description')}
        </p>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('dashboard.overview.purpose')}
        </p>
        <FeatureList items={t('dashboard.overview.components', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('dashboard.kpis.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('dashboard.kpis.description')}
        </p>
        <FeatureList items={t('dashboard.kpis.metrics', { returnObjects: true })} />
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          {t('dashboard.kpis.interpretation')}
        </p>
        <Tip type="tip">{t('dashboard.kpis.tip')}</Tip>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('dashboard.quickActions.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('dashboard.quickActions.description')}
        </p>
        <FeatureList items={t('dashboard.quickActions.actions', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('dashboard.activeOrders.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('dashboard.activeOrders.description')}
        </p>
        <FeatureList items={t('dashboard.activeOrders.columns', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('dashboard.alerts.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('dashboard.alerts.description')}
        </p>
        <FeatureList items={t('dashboard.alerts.types', { returnObjects: true })} />
      </div>
    </div>
  );

  const renderOrders = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('orders.overview.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('orders.overview.description')}
        </p>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('orders.overview.importance')}
        </p>
        <p className="text-gray-600 dark:text-gray-400">
          {t('orders.overview.sources')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('orders.create.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('orders.create.description')}
        </p>
        <StepList steps={t('orders.create.steps', { returnObjects: true })} />
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          {t('orders.create.requiredFields')}
        </p>
        <Tip type="tip">{t('orders.create.tip')}</Tip>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('orders.statuses.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('orders.statuses.description')}
        </p>
        <FeatureList items={t('orders.statuses.list', { returnObjects: true })} />
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          {t('orders.statuses.transitions')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('orders.assignment.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('orders.assignment.description')}
        </p>
        <StepList steps={t('orders.assignment.steps', { returnObjects: true })} />
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          {t('orders.assignment.autoAssignment')}
        </p>
        <Tip type="tip">{t('orders.assignment.tip')}</Tip>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('orders.lifecycle.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('orders.lifecycle.description')}
        </p>
        <FeatureList items={t('orders.lifecycle.actions', { returnObjects: true })} />
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          {t('orders.lifecycle.permissions')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('orders.filters.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('orders.filters.description')}
        </p>
        <FeatureList items={t('orders.filters.options', { returnObjects: true })} />
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          {t('orders.filters.savedFilters')}
        </p>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('orders.filters.export')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('orders.details.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('orders.details.description')}
        </p>
        <FeatureList items={t('orders.details.sections', { returnObjects: true })} />
      </div>
    </div>
  );

  const renderPatients = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('patients.overview.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('patients.overview.description')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('patients.register.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('patients.register.description')}
        </p>
        <StepList steps={t('patients.register.steps', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('patients.customFields.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('patients.customFields.description')}
        </p>
        <FeatureList items={t('patients.customFields.features', { returnObjects: true })} />
        <Tip type="tip">{t('patients.customFields.tip')}</Tip>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('patients.clinicalHistory.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('patients.clinicalHistory.description')}
        </p>
        <FeatureList items={t('patients.clinicalHistory.features', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('patients.importExport.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('patients.importExport.description')}
        </p>
        <FeatureList items={t('patients.importExport.steps', { returnObjects: true })} />
      </div>
    </div>
  );

  const renderSpecialists = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('specialists.overview.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('specialists.overview.description')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('specialists.directory.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('specialists.directory.description')}
        </p>
        <FeatureList items={t('specialists.directory.info', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('specialists.register.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('specialists.register.description')}
        </p>
        <StepList steps={t('specialists.register.steps', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('specialists.types.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('specialists.types.description')}
        </p>
        <FeatureList items={t('specialists.types.examples', { returnObjects: true })} />
        <Tip type="tip">{t('specialists.types.tip')}</Tip>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('specialists.calendar.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('specialists.calendar.description')}
        </p>
        <FeatureList items={t('specialists.calendar.features', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('specialists.customFields.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('specialists.customFields.description')}
        </p>
      </div>
    </div>
  );

  const renderMedications = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('medications.overview.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('medications.overview.description')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('medications.catalog.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('medications.catalog.description')}
        </p>
        <FeatureList items={t('medications.catalog.info', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('medications.inventory.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('medications.inventory.description')}
        </p>
        <FeatureList items={t('medications.inventory.features', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('medications.enterprise.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('medications.enterprise.description')}
        </p>
        <FeatureList items={t('medications.enterprise.features', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('medications.barcodes.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('medications.barcodes.description')}
        </p>
        <StepList steps={t('medications.barcodes.steps', { returnObjects: true })} />
      </div>
    </div>
  );

  const renderReports = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('reports.overview.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('reports.overview.description')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('reports.predefined.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('reports.predefined.description')}
        </p>
        <FeatureList items={t('reports.predefined.list', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('reports.custom.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('reports.custom.description')}
        </p>
        <StepList steps={t('reports.custom.steps', { returnObjects: true })} />
        <Tip type="tip">{t('reports.custom.tip')}</Tip>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('reports.advanced.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('reports.advanced.description')}
        </p>
        <FeatureList items={t('reports.advanced.examples', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('reports.geolocation.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('reports.geolocation.description')}
        </p>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('settings.overview.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('settings.overview.description')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('settings.language.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('settings.language.description')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('settings.aiOcr.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('settings.aiOcr.description')}
        </p>
        <FeatureList items={t('settings.aiOcr.settings', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('settings.email.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('settings.email.description')}
        </p>
        <FeatureList items={t('settings.email.settings', { returnObjects: true })} />
        <Tip type="tip">{t('settings.email.tip')}</Tip>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('settings.templates.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('settings.templates.description')}
        </p>
        <FeatureList items={t('settings.templates.templates', { returnObjects: true })} />
        <h4 className="font-semibold mt-4 mb-2 text-gray-800 dark:text-gray-200">Características:</h4>
        <FeatureList items={t('settings.templates.features', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('settings.maps.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('settings.maps.description')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('settings.coverage.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('settings.coverage.description')}
        </p>
        <FeatureList items={t('settings.coverage.features', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('settings.workflows.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('settings.workflows.description')}
        </p>
        <FeatureList items={t('settings.workflows.settings', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('settings.seed.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('settings.seed.description')}
        </p>
        <Tip type="warning">Esta acción eliminará todos los datos existentes. Usar solo en ambientes de prueba.</Tip>
      </div>
    </div>
  );

  const renderAutomation = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('automation.overview.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('automation.overview.description')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('automation.rules.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('automation.rules.description')}
        </p>
        <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">Tipos de reglas:</h4>
        <FeatureList items={t('automation.rules.types', { returnObjects: true })} />
        <h4 className="font-semibold mt-4 mb-2 text-gray-800 dark:text-gray-200">Configuración:</h4>
        <FeatureList items={t('automation.rules.config', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('automation.businessHours.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('automation.businessHours.description')}
        </p>
        <FeatureList items={t('automation.businessHours.settings', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('automation.holidays.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('automation.holidays.description')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('automation.serviceZones.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('automation.serviceZones.description')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('automation.scheduledTasks.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('automation.scheduledTasks.description')}
        </p>
        <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">Tipos:</h4>
        <FeatureList items={t('automation.scheduledTasks.types', { returnObjects: true })} />
        <h4 className="font-semibold mt-4 mb-2 text-gray-800 dark:text-gray-200">Estados:</h4>
        <FeatureList items={t('automation.scheduledTasks.statuses', { returnObjects: true })} />
      </div>
    </div>
  );

  const renderOcr = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('ocr.overview.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('ocr.overview.description')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('ocr.upload.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('ocr.upload.description')}
        </p>
        <StepList steps={t('ocr.upload.steps', { returnObjects: true })} />
        <Tip type="note">{t('ocr.upload.formats')}</Tip>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('ocr.extraction.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('ocr.extraction.description')}
        </p>
        <FeatureList items={t('ocr.extraction.data', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('ocr.history.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('ocr.history.description')}
        </p>
        <FeatureList items={t('ocr.history.statuses', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('ocr.automation.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('ocr.automation.description')}
        </p>
        <StepList steps={t('ocr.automation.flow', { returnObjects: true })} />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('ocr.alternativeNames.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('ocr.alternativeNames.description')}
        </p>
        <FeatureList items={t('ocr.alternativeNames.items', { returnObjects: true })} />
        <Tip type="tip">{t('ocr.alternativeNames.example')}</Tip>
      </div>
    </div>
  );

  const renderSecurity = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('security.overview.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('security.overview.description')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('security.users.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('security.users.description')}
        </p>
        <FeatureList items={t('security.users.actions', { returnObjects: true })} />
        <Tip type="tip">{t('security.users.tip')}</Tip>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('security.roles.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('security.roles.description')}
        </p>
        <FeatureList items={t('security.roles.defaultRoles', { returnObjects: true })} />
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          {t('security.roles.permissions')}
        </p>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('security.apiTokens.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('security.apiTokens.description')}
        </p>
        <FeatureList items={t('security.apiTokens.features', { returnObjects: true })} />
        <Tip type="warning">{t('security.apiTokens.warning')}</Tip>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {t('security.audit.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('security.audit.description')}
        </p>
        <FeatureList items={t('security.audit.logged', { returnObjects: true })} />
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          {t('security.audit.filters')}
        </p>
      </div>
    </div>
  );

  const renderFaq = () => {
    const questions = t('faq.questions', { returnObjects: true });
    return (
      <div className="space-y-4">
        <Accordion type="single" collapsible className="w-full">
          {questions.map((item, index) => (
            <AccordionItem key={index} value={`faq-${index}`}>
              <AccordionTrigger className="text-left text-gray-900 dark:text-white hover:text-red-600">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 dark:text-gray-400">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    );
  };

  const renderers = {
    gettingStarted: renderGettingStarted,
    dashboard: renderDashboard,
    orders: renderOrders,
    patients: renderPatients,
    specialists: renderSpecialists,
    medications: renderMedications,
    reports: renderReports,
    settings: renderSettings,
    automation: renderAutomation,
    ocr: renderOcr,
    security: renderSecurity,
    faq: renderFaq
  };

  const renderer = renderers[sectionKey];
  return renderer ? renderer() : null;
};

// Main Documentation Page
export default function DocumentationPage() {
  const { t } = useTranslation('documentation');
  const [activeSection, setActiveSection] = useState('gettingStarted');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState(['gettingStarted']);

  const sections = [
    'gettingStarted',
    'dashboard',
    'orders',
    'patients',
    'specialists',
    'medications',
    'reports',
    'settings',
    'automation',
    'ocr',
    'security',
    'faq'
  ];

  // Filter sections based on search
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return sections;
    const query = searchQuery.toLowerCase();
    return sections.filter(section => {
      const sectionName = t(`sections.${section}`).toLowerCase();
      const sectionTitle = t(`${section}.title`, '').toLowerCase();
      return sectionName.includes(query) || sectionTitle.includes(query);
    });
  }, [searchQuery, t]);

  const handleSectionClick = (section) => {
    setActiveSection(section);
    if (!expandedSections.includes(section)) {
      setExpandedSections([...expandedSections, section]);
    }
  };

  const Icon = sectionIcons[activeSection] || Book;

  return (
    <div className="h-[calc(100vh-4rem)] flex" data-testid="documentation-page">
      {/* Sidebar */}
      <div className="w-72 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <Book className="h-6 w-6 text-red-600" />
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              {t('title')}
            </h1>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="documentation-search"
            />
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2">
            <p className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('tableOfContents')}
            </p>
            <nav className="space-y-1">
              {filteredSections.map((section) => {
                const SectionIcon = sectionIcons[section];
                const isActive = activeSection === section;
                return (
                  <button
                    key={section}
                    onClick={() => handleSectionClick(section)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 font-medium'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                    data-testid={`doc-section-${section}`}
                  >
                    <SectionIcon className="h-4 w-4" />
                    <span>{t(`sections.${section}`)}</span>
                    {isActive && (
                      <ChevronRight className="h-4 w-4 ml-auto" />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </ScrollArea>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden bg-gray-50 dark:bg-gray-950">
        <ScrollArea className="h-full">
          <div className="max-w-4xl mx-auto p-8">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/30">
                <Icon className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t(`sections.${activeSection}`)}
                </h2>
                <p className="text-gray-500 dark:text-gray-400">
                  {t('subtitle')}
                </p>
              </div>
            </div>

            {/* Section Content */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <SectionContent sectionKey={activeSection} />
              </CardContent>
            </Card>

            {/* Navigation Footer */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              {sections.indexOf(activeSection) > 0 && (
                <button
                  onClick={() => handleSectionClick(sections[sections.indexOf(activeSection) - 1])}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  <ChevronRight className="h-4 w-4 rotate-180" />
                  <span>{t(`sections.${sections[sections.indexOf(activeSection) - 1]}`)}</span>
                </button>
              )}
              <div className="flex-1" />
              {sections.indexOf(activeSection) < sections.length - 1 && (
                <button
                  onClick={() => handleSectionClick(sections[sections.indexOf(activeSection) + 1])}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  <span>{t(`sections.${sections[sections.indexOf(activeSection) + 1]}`)}</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
